"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { createClient } from "@/lib/supabase/client";
import { useState } from "react";
import { subscriptionService } from '@/lib/stripe/subscriptionService';
import Link from "next/link";

import Sidebar from "./Sidebar";

export default function ProtectedRoute({
  children,
  adminOnly = false
}: {
  children: React.ReactNode;
  adminOnly?: boolean;
}) {
  const router = useRouter();
  const { user, profile, loading } = useAuth();
  const [isApproved, setIsApproved] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (!loading) {
        if (!user) {
          router.push("/login");
          return;
        }

        // Check if admin first (from metadata for speed)
        const isAdmin = user.user_metadata?.role === "admin" || user.app_metadata?.role === "admin";

        if (isAdmin) {
          setIsApproved(true);
          setHasAccess(true);
          setChecking(false);
          return;
        }

        // Redirect if route is admin only and user is not admin
        if (adminOnly && !isAdmin) {
          router.push("/search-page");
          return;
        }

        // Use cached profile from context if available
        const userProfile = profile;

        if (userProfile) {
          if (userProfile.status !== "approved") {
            setIsPending(true);
            setChecking(false);
            return;
          }
          setIsApproved(true);
        } else {
          // If profile is not yet in context, wait or do a quick fetch
          return;
        }

        // Check subscription access (with payment override)
        const canAccess = await subscriptionService.canAccessPlatform(user.id);

        if (!canAccess) {
          router.push("/subscription");
          return;
        }

        setHasAccess(true);
        setChecking(false);
      }
    };

    checkAccess();
  }, [user, profile, loading, router]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal mx-auto mb-4"></div>
          <p className="text-muted">Verifying secure access...</p>
        </div>
      </div>
    );
  }

  // Show pending approval message instead of blank/redirect loop
  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand px-6">
        <div className="max-w-md w-full bg-white rounded-lg border border-border p-8 shadow-sm text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-heading font-bold text-navy text-xl mb-2">Account Pending Approval</h2>
          <p className="text-muted text-sm mb-6">
            Your account is awaiting admin approval. You will receive an email once your account has been approved and you can access the platform.
          </p>
          <Link
            href="/login"
            className="inline-flex items-center justify-center px-6 py-2.5 bg-teal text-white rounded font-medium hover:bg-teal-600 transition-colors text-sm"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!isApproved || !hasAccess) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-sand">
      <Sidebar />
      <div className="flex-1 lg:pl-64 flex flex-col min-h-screen">
        {children}
      </div>
    </div>
  );
}